import {
  collection,
  query,
  orderBy,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  where,
  setDoc,
} from 'firebase/firestore';
import { getDb } from './firebase';
import { NewsCategory } from '../types';
import { slugify } from '@2bforest/common/utils';

const COLLECTION_NAME = 'news_categories';

/**
 * List all news categories
 */
export async function listCategories(): Promise<NewsCategory[]> {
  try {
    const q = query(collection(getDb(), COLLECTION_NAME), orderBy('id', 'asc'));
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    })) as NewsCategory[];
  } catch (error) {
    console.error('Error listing categories:', error);
    throw error;
  }
}

/**
 * List all active news categories for dropdowns
 */
export async function listActiveCategories(): Promise<NewsCategory[]> {
  try {
    const q = query(
      collection(getDb(), COLLECTION_NAME),
      where('isActive', '==', true),
      orderBy('id', 'asc')
    );

    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    })) as NewsCategory[];
  } catch (error) {
    console.error('Error listing categories:', error);
    throw error;
  }
}

/**
 * Create a new category with slugified ID
 */
export async function createCategory(categoryData: Omit<NewsCategory, 'id'>): Promise<string> {
  try {
    // Generate slugified ID from the category name
    const categoryId = slugify(categoryData.name);

    // Use setDoc with custom ID instead of addDoc
    await setDoc(doc(getDb(), COLLECTION_NAME, categoryId), {
      ...categoryData,
      id: categoryId,
    });

    return categoryId;
  } catch (error) {
    console.error('Error creating category:', error);
    throw error;
  }
}

/**
 * Update an existing category
 */
export async function updateCategory(
  categoryId: string,
  categoryData: Partial<Omit<NewsCategory, 'id'>>
): Promise<void> {
  try {
    await updateDoc(doc(getDb(), COLLECTION_NAME, categoryId), categoryData);
  } catch (error) {
    console.error('Error updating category:', error);
    throw error;
  }
}

/**
 * Delete a category
 */
export async function deleteCategory(categoryId: string): Promise<void> {
  try {
    await deleteDoc(doc(getDb(), COLLECTION_NAME, categoryId));
  } catch (error) {
    console.error('Error deleting category:', error);
    throw error;
  }
}

/**
 * Toggle the active status of a category
 */
export async function toggleCategoryActive(categoryId: string, isActive: boolean): Promise<void> {
  try {
    await updateDoc(doc(getDb(), COLLECTION_NAME, categoryId), { isActive });
  } catch (error) {
    console.error('Error toggling category active status:', error);
    throw error;
  }
}
